import { ReactElement, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  HiOutlineArrowTopRightOnSquare,
} from 'react-icons/hi2';
import CollapsiblePanel from
  '../RunView/RunDiagnostic/CollapsiblePanel';
import EquipmentCoordination from
  './EquipmentCoordination';
import ParametersCoordination from
  './ParametersCoordination';

interface CoordinationTabProps {
  sessions: any[] | undefined;
  selectedSessionId: string;
  equipmentCatalog: any;
  ridersById: Map<string, any> | undefined;
}

function formatDate(session: any): string {
  if (session.date) return session.date;
  if (session.created_at) {
    return new Date(session.created_at)
      .toLocaleDateString();
  }
  return '';
}

function getRiderName(
  session: any,
  ridersById?: Map<string, any>,
): string {
  if (!ridersById || !session.rider_id) return '-';
  const rider = ridersById.get(session.rider_id);
  return rider?.nickname ?? rider?.name ?? '-';
}

export default function CoordinationTab({
  sessions,
  selectedSessionId,
  equipmentCatalog,
  ridersById,
}: CoordinationTabProps): ReactElement {
  const navigate = useNavigate();
  const [equipmentOpen, setEquipmentOpen] =
    useState(true);
  const [parametersOpen, setParametersOpen] =
    useState(false);

  if (!sessions || sessions.length === 0) {
    return (
      <div
        className={
          'cr-flex cr-h-32 cr-items-center '
          + 'cr-justify-center cr-text-gray-400'
        }
      >
        Select a reference session above to view
        coordination options.
      </div>
    );
  }

  const parentSession = sessions[0];
  const childSessions = sessions.slice(1);

  return (
    <div className="cr-space-y-6">
      {/* Session Tree */}
      <div
        className={
          'cr-rounded-lg cr-border-2 '
          + 'cr-border-cirrusBlue-100 '
          + 'cr-bg-white cr-overflow-hidden'
        }
      >
        <div
          className={
            'cr-bg-cirrusBlue-50 cr-px-4 cr-py-2 '
            + 'cr-text-sm cr-font-semibold '
            + 'cr-uppercase cr-text-cirrusBlue-400'
          }
        >
          Session Hierarchy
        </div>
        <table
          className={
            'cr-w-full cr-text-sm cr-text-[#03365D]'
          }
        >
          <thead>
            <tr
              className={
                'cr-border-b cr-border-gray-200 '
                + 'cr-text-xs cr-uppercase '
                + 'cr-text-gray-500'
              }
            >
              <th className="cr-px-4 cr-py-2 cr-text-left">
                Session
              </th>
              <th className="cr-px-4 cr-py-2 cr-text-left">
                Date
              </th>
              <th className="cr-px-4 cr-py-2 cr-text-left">
                Rider
              </th>
              <th className="cr-px-4 cr-py-2 cr-text-left">
                Description
              </th>
              <th className="cr-px-4 cr-py-2 cr-w-10" />
            </tr>
          </thead>
          <tbody>
            {/* Parent row */}
            <tr
              className={
                'cr-border-b cr-border-gray-200 '
                + 'cr-bg-cirrusBlue-25 cr-font-semibold'
              }
            >
              <td className="cr-px-4 cr-py-2">
                Reference
              </td>
              <td className="cr-px-4 cr-py-2">
                {formatDate(parentSession)}
              </td>
              <td className="cr-px-4 cr-py-2">
                {getRiderName(parentSession, ridersById)}
              </td>
              <td className="cr-px-4 cr-py-2">
                {parentSession.description
                  || parentSession.name
                  || '-'}
              </td>
              <td className="cr-px-4 cr-py-2 cr-text-center">
                <button
                  onClick={() =>
                    navigate(
                      `/sessions/${parentSession.id}`,
                    )
                  }
                  className={
                    'cr-text-cirrusBlue-400 '
                    + 'hover:cr-text-blue-600 '
                    + 'cr-transition-colors'
                  }
                  title="Go to session"
                >
                  <HiOutlineArrowTopRightOnSquare
                    className="cr-size-5"
                  />
                </button>
              </td>
            </tr>
            {/* Child rows */}
            {childSessions.map((child: any) => (
              <tr
                key={child.id}
                className={
                  'cr-border-b cr-border-gray-100'
                }
              >
                <td className="cr-px-4 cr-py-2 cr-pl-8">
                  Comparison
                </td>
                <td className="cr-px-4 cr-py-2">
                  {formatDate(child)}
                </td>
                <td className="cr-px-4 cr-py-2">
                  {getRiderName(child, ridersById)}
                </td>
                <td className="cr-px-4 cr-py-2">
                  {child.description
                    || child.name
                    || '-'}
                </td>
                <td
                  className={
                    'cr-px-4 cr-py-2 cr-text-center'
                  }
                >
                  <button
                    onClick={() =>
                      navigate(
                        `/sessions/${child.id}`,
                      )
                    }
                    className={
                      'cr-text-cirrusBlue-400 '
                      + 'hover:cr-text-blue-600 '
                      + 'cr-transition-colors'
                    }
                    title="Go to session"
                  >
                    <HiOutlineArrowTopRightOnSquare
                      className="cr-size-5"
                    />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Collapsible Categories */}
      <CollapsiblePanel
        title="Equipment"
        isOpen={equipmentOpen}
        onToggle={() => setEquipmentOpen(!equipmentOpen)}
        noScroll
      >
        <EquipmentCoordination
          sessions={sessions}
          equipmentCatalog={equipmentCatalog}
          selectedSessionId={selectedSessionId}
        />
      </CollapsiblePanel>

      <CollapsiblePanel
        title="Parameters"
        isOpen={parametersOpen}
        onToggle={() =>
          setParametersOpen(!parametersOpen)
        }
        noScroll
      >
        <ParametersCoordination
          sessions={sessions}
          selectedSessionId={selectedSessionId}
        />
      </CollapsiblePanel>
    </div>
  );
}
